import React from "react";
import { useCoreContext } from "../../../../context/global/CoreContext";
import { ClipboardList } from "../../../../components/icons/svg/outline";
import { REPORT_TYPE_OPTIONS, generateTMId } from "../constants";

const TITLE_MAX_LENGTH = 25;

/**
 * ReportInformationSection Component
 * Displays/edits report information for T&M forms
 *
 * @param {Object} props
 * @param {Object} props.data - Current report information values.
 * @param {Function} props.onChange - Callback for ref-backed report information changes.
 * @param {boolean} props.readOnly - Whether all report information fields are read-only.
 * @param {boolean} props.titleReadOnly - Whether the internal title is read-only.
 * @param {string|null} props.nextTMId - Generated T&M ID for unsaved reports.
 * @returns {JSX.Element} Report information section.
 */
const ReportInformationSection = ({
  data,
  onChange,
  readOnly = false,
  titleReadOnly = false,
  nextTMId = null,
}) => {
  const { allJobsList } = useCoreContext();
  const [titleCharactersRemaining, setTitleCharactersRemaining] =
    React.useState(TITLE_MAX_LENGTH - (data?.title?.length || 0));

  const handleReportTypeChange = (e) => {
    onChange?.({ ...data, report_type: e.target.value });
  };

  const handlePcoChange = (e) => {
    onChange?.({ ...data, pco_number: e.target.value });
  };

  const handleContractorChange = (e) => {
    onChange?.({ ...data, contractor_name: e.target.value });
  };

  const handleTitleChange = (e) => {
    setTitleCharactersRemaining(TITLE_MAX_LENGTH - e.target.value.length);
    onChange?.({ ...data, title: e.target.value });
  };

  const selectedJob = allJobsList?.find((job) => job.id === data?.job);
  // Priority: 1) data.tm_id (from API), 2) nextTMId (for new reports), 3) generate from existing data
  const tmId = data?.tm_id || nextTMId || generateTMId(selectedJob?.number, data?.id);

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
        <ClipboardList className="w-5 h-5 text-emerald-800" />
        Report Information {readOnly && "(Read Only)"}
      </h2>

      <div className="grid grid-cols-3 gap-1">
        {/* Report Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Report Type
          </label>
          {readOnly ? (
            <div className="px-1 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
              {REPORT_TYPE_OPTIONS.find(
                (opt) => opt.value === data?.report_type
              )?.label || "N/A"}
            </div>
          ) : (
            <select
              defaultValue={data?.report_type || "City_DFA"}
              onChange={handleReportTypeChange}
              className="w-full h-[37px] px-1 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {REPORT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* T&M ID */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            T&M ID
          </label>
          <div className="h-[37px] px-1 py-1.5 bg-gray-50 border border-gray-300 rounded-md text-gray-700 font-mono">
            {tmId}
          </div>
        </div>

        {/* PCO # (Optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            PCO # (Optional)
          </label>
          {readOnly ? (
            <div className="h-[37px] px-1 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
              {data?.pco_number || ""}
            </div>
          ) : (
            <input
              type="text"
              defaultValue={data?.pco_number || ""}
              onChange={handlePcoChange}
              placeholder="Change Order #"
              className="w-full h-[37px] px-1 py-2 caret-black border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Contractor Name *
          </label>
          {readOnly ? (
            <div className="h-[37px] px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
              {data?.contractor_name || ""}
            </div>
          ) : (
            <input
              type="text"
              defaultValue={data?.contractor_name || "R&S Construction Management Inc."}
              onChange={handleContractorChange}
              placeholder="Enter Contractor Name"
              className="w-full h-[37px] px-3 py-2 caret-black border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Title (Internal use only)
          </label>
          {readOnly || titleReadOnly ? (
            <div className="h-[37px] px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
              {data?.title || ""}
            </div>
          ) : (
            <>
              <input
                type="text"
                defaultValue={data?.title || ""}
                maxLength={TITLE_MAX_LENGTH}
                onChange={handleTitleChange}
                placeholder="Enter internal title"
                className="w-full h-[37px] px-3 py-2 caret-black border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="mt-1 text-right text-xs text-gray-500">
                {titleCharactersRemaining} characters remaining
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportInformationSection;
